import {
  MediaCaptionsButton,
  MediaChromeButton,
  MediaControlBar,
  MediaFullscreenButton,
  MediaMuteButton,
  MediaPlayButton,
  MediaVolumeRange
} from 'media-chrome/dist/react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentRef
} from 'react';
import { isAndroid, isIOS } from 'react-device-detect';
import { useSessionstorageState } from 'rooks';
import shallow from 'zustand/shallow';

import { useIsBreakpointWidth } from 'lib/hooks/useIsBreakpointWidth';

import useVideoPlayerStore from 'components/video-player/store';

import FullScreenIcon from 'icons/FullscreenIcon';
import PauseIcon from 'icons/PauseIcon';
import PlayIcon from 'icons/PlayIcon';
import SettingsIcon from 'icons/SettingsIcon';
import SubtitlesIcon from 'icons/SubtitleIcon';
import VolumeIcon from 'icons/VolumeIcon';

import type { Video } from 'api';
import VideoPlayerControlPanel from 'components/video-player/controls/panel';
import SettingsPanel from 'components/video-player/controls/settings';
import SubtitlesPanel from 'components/video-player/controls/subtitles';
import { timestampToSeconds } from 'lib/util';
import * as styles from '../Player.styles';

type AdControlsProps = {
  adTimeRemaining: number;
  playerTalkData: Video['acmePlayerData'];
};

export default function AdControls({
  adTimeRemaining,
  playerTalkData
}: AdControlsProps) {
  const volumeRangeRef = useRef<ComponentRef<typeof MediaVolumeRange>>(null);
  const isMobileDevice = useMemo(() => isAndroid || isIOS, []);
  const isMobileorTabletWeb = useIsBreakpointWidth({
    size: 'xl',
    breakPointType: 'tui'
  });
  const isMobile = isMobileDevice || isMobileorTabletWeb;

  const [visibleTooltip, setVisibleTooltip] = useState<
    'subtitles' | 'settings' | null
  >(null);

  // Handlers to open each tooltip
  const openSubtitlesTooltip = () => setVisibleTooltip('subtitles');
  const openSettingsTooltip = () => setVisibleTooltip('settings');

  // Handler to close any tooltip
  const closeTooltip = () => setVisibleTooltip(null);

  const { ad, audio, requestMute, requestVolume, onFullscreen } =
    useVideoPlayerStore(
      state => ({
        ...state
      }),
      shallow
    );
  const [muted, setMuted] = useSessionstorageState('muted_state', !!audio.mute);

  const toggleMute = useCallback(() => {
    const newMutedState = !muted;
    setMuted(newMutedState);
    requestMute(newMutedState, true);
  }, [muted, requestMute, setMuted]);

  const progressPercentage =
    ((ad?.adInfo?.duration - timestampToSeconds(adTimeRemaining.toString())) /
      ad?.adInfo?.duration) *
    100;

  useEffect(() => {
    const shadowRoot = volumeRangeRef.current?.shadowRoot;
    const volumeInput = shadowRoot?.querySelector('input[type="range"]');
    const handleVolumeChange = (event: Event) => {
      setMuted(false);
      requestMute(false, false);
      const newVolume = parseFloat((event.target as HTMLInputElement).value);
      requestVolume(newVolume, true);
    };
    if (volumeInput) {
      volumeInput.addEventListener('input', handleVolumeChange);
    }
    return () => {
      if (volumeInput) {
        volumeInput.removeEventListener('input', handleVolumeChange);
      }
    };
  }, [audio.volume, muted]);

  return (
    <>
      <MediaControlBar
        id="ad-control-bar"
        className="pointer-events-auto absolute bottom-0 flex w-full items-center justify-between bg-transparent bg-gradient-to-t from-black to-transparent px-2 py-2"
        style={styles.controlBarStyles as React.CSSProperties}
      >
        <div className="flex items-center">
          <MediaPlayButton
            style={styles.noHoverColorStyles as React.CSSProperties}
          >
            <span slot="play">
              <PlayIcon variant="default" className="h-5 overflow-visible" />
            </span>
            <span slot="pause">
              <PauseIcon variant="default" className="h-5 overflow-visible" />
            </span>
          </MediaPlayButton>
        </div>

        <div className="mx-4 flex-grow">
          <div className="h-1 w-full overflow-hidden rounded-[10px] bg-white bg-opacity-[0.64]">
            <div
              className="h-1 bg-ted-red"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>

        <div className="media-volume-wrapper">
          <button onClick={toggleMute} type="button" id="media-mute-button">
            <MediaMuteButton
              style={styles.noHoverColorStyles as React.CSSProperties}
              mediaVolumeLevel={audio.volume.toString()}
            >
              <span slot="off">
                <VolumeIcon variant="mute" className="h-5 overflow-visible" />
              </span>

              <span slot="low">
                <VolumeIcon variant="mid" className="h-5 overflow-visible" />
              </span>

              <span slot="medium">
                <VolumeIcon variant="mid" className="h-5 overflow-visible" />
              </span>

              <span slot="high">
                <VolumeIcon variant="max" className="h-5 overflow-visible" />
              </span>
            </MediaMuteButton>
          </button>

          <div className="media-volume-range-wrapper absolute -left-[55px] -top-[105px] hidden h-10 w-40 -rotate-90 items-center justify-center rounded-[4px] bg-white p-4 opacity-0 lg:flex">
            <MediaVolumeRange
              ref={volumeRangeRef}
              mediaVolume={audio.volume}
              style={{
                ...styles.volumeRangeStyles,
                ...styles.noHoverColorStyles
              }}
            />
          </div>
        </div>

        <div
          className="media-subtitles-wrapper flex items-center justify-center"
          onMouseEnter={openSubtitlesTooltip}
          onFocus={openSubtitlesTooltip}
          onMouseLeave={closeTooltip}
          onBlur={closeTooltip}
          id="subtitle-control"
        >
          <VideoPlayerControlPanel
            isVisible={visibleTooltip === 'subtitles'}
            content={<SubtitlesPanel languages={playerTalkData.languages} />}
            animation="scale"
            maxWidth="none"
            placement={isMobile ? 'bottom' : 'top'}
          >
            <div className="media-caption-wrapper">
              <MediaCaptionsButton
                className="px-2 lg-tui:px-[10px]"
                disabled
                style={styles.noHoverColorStyles as React.CSSProperties}
              >
                <span slot="icon">
                  <SubtitlesIcon
                    variant="default"
                    className="h-5 overflow-visible"
                  />
                </span>
              </MediaCaptionsButton>
            </div>
          </VideoPlayerControlPanel>
        </div>

        <div
          className="media-settings-wrapper flex items-center justify-center"
          onMouseEnter={openSettingsTooltip}
          onFocus={openSettingsTooltip}
          onMouseLeave={closeTooltip}
          onBlur={closeTooltip}
          id="settings-control"
        >
          <VideoPlayerControlPanel
            adaptive
            isVisible={visibleTooltip === 'settings'}
            delay={100}
            animation="scale"
            placement={isMobile ? 'bottom' : 'top'}
            content={<SettingsPanel showSpeeds={false} />}
          >
            <div>
              <MediaChromeButton
                className="px-1 lg-tui:px-[10px]"
                disabled
                style={styles.noHoverColorStyles as React.CSSProperties}
              >
                <slot name="icon">
                  <SettingsIcon className="h-5 overflow-visible" />
                </slot>
              </MediaChromeButton>
            </div>
          </VideoPlayerControlPanel>
        </div>

        <div className="flex items-center">
          <MediaFullscreenButton
            style={styles.noHoverColorStyles as React.CSSProperties}
            onClick={() => onFullscreen(!document.fullscreenElement)}
          >
            <span slot="enter">
              <FullScreenIcon
                variant="off"
                className="h-4 overflow-visible lg-tui:h-5"
              />
            </span>
            <span slot="exit">
              <FullScreenIcon
                variant="on"
                className="h-4 overflow-visible lg-tui:h-5"
              />
            </span>
          </MediaFullscreenButton>
        </div>
      </MediaControlBar>
    </>
  );
}
